import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  fetchModuleLearningObjectives, 
  createLearningObjective, 
  updateLearningObjective, 
  deleteLearningObjective 
} from '@/services/modules/objectiveService';
import { syncModuleTaxonomyLevels } from '@/services/modules/moduleService';
import { LearningObjective, TaxonomyLevel } from '@/types/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Card, CardContent } from '@/components/ui/card';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Loader2, Save, PlusCircle, Edit, Trash2, RefreshCw } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import TaxonomyBadge from '@/components/learning/TaxonomyBadge';

const objectiveFormSchema = z.object({
  description: z.string().min(3, { message: 'Description must be at least 3 characters' }),
  taxonomy_level: z.enum(['remember', 'understand', 'apply', 'analyze', 'evaluate', 'create']),
});

interface LearningObjectiveManagerProps {
  moduleId: string;
}

const LearningObjectiveManager: React.FC<LearningObjectiveManagerProps> = ({ moduleId }) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [editingObjective, setEditingObjective] = useState<LearningObjective | null>(null);
  const [isAddingNew, setIsAddingNew] = useState(false);

  const form = useForm<z.infer<typeof objectiveFormSchema>>({
    resolver: zodResolver(objectiveFormSchema),
    defaultValues: {
      description: '',
      taxonomy_level: 'understand',
    },
  });

  const { data: objectives, isLoading } = useQuery({
    queryKey: ['moduleObjectives', moduleId],
    queryFn: () => fetchModuleLearningObjectives(moduleId),
    enabled: !!moduleId,
  });

  const createObjectiveMutation = useMutation({
    mutationFn: createLearningObjective,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['moduleObjectives', moduleId] });
      toast({
        title: 'Objective created',
        description: 'Learning objective has been created successfully',
      });
      resetForm();
      syncTaxonomyLevels();
    },
    onError: (error) => {
      toast({
        title: 'Failed to create objective',
        description: 'There was an error creating the learning objective',
        variant: 'destructive',
      });
      console.error('Error creating learning objective:', error);
    },
  });

  const updateObjectiveMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<LearningObjective> }) => 
      updateLearningObjective(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['moduleObjectives', moduleId] });
      toast({
        title: 'Objective updated',
        description: 'Learning objective has been updated successfully',
      });
      resetForm();
      syncTaxonomyLevels();
    },
    onError: (error) => {
      toast({
        title: 'Failed to update objective',
        description: 'There was an error updating the learning objective',
        variant: 'destructive',
      });
      console.error('Error updating learning objective:', error);
    },
  });

  const deleteObjectiveMutation = useMutation({
    mutationFn: deleteLearningObjective,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['moduleObjectives', moduleId] });
      toast({
        title: 'Objective deleted',
        description: 'Learning objective has been deleted successfully',
      });
      syncTaxonomyLevels();
    },
    onError: (error) => {
      toast({
        title: 'Failed to delete objective',
        description: 'There was an error deleting the learning objective',
        variant: 'destructive',
      });
      console.error('Error deleting learning objective:', error);
    },
  });

  const syncTaxonomyLevelsMutation = useMutation({
    mutationFn: syncModuleTaxonomyLevels,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['modules'] });
      toast({
        title: 'Taxonomy levels synced',
        description: 'Module taxonomy levels have been updated based on learning objectives',
      });
    },
    onError: (error) => {
      toast({
        title: 'Failed to sync taxonomy levels',
        description: 'There was an error syncing taxonomy levels',
        variant: 'destructive',
      });
      console.error('Error syncing taxonomy levels:', error);
    },
  });

  useEffect(() => {
    if (editingObjective) {
      form.reset({
        description: editingObjective.description,
        taxonomy_level: editingObjective.taxonomy_level,
      });
      setIsAddingNew(true);
    }
  }, [editingObjective, form]);

  const resetForm = () => {
    form.reset({
      description: '',
      taxonomy_level: 'understand',
    });
    setEditingObjective(null);
    setIsAddingNew(false);
  };

  const onSubmit = (values: z.infer<typeof objectiveFormSchema>) => {
    if (editingObjective) {
      updateObjectiveMutation.mutate({
        id: editingObjective.id,
        data: values,
      });
    } else {
      createObjectiveMutation.mutate({
        module_id: moduleId,
        description: values.description,
        taxonomy_level: values.taxonomy_level,
      });
    }
  };

  const handleDelete = (id: string) => {
    if (confirm('Are you sure you want to delete this learning objective?')) {
      deleteObjectiveMutation.mutate(id);
    }
  };

  const syncTaxonomyLevels = () => {
    syncTaxonomyLevelsMutation.mutate(moduleId);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-6">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">Learning Objectives</h3>
        <div className="flex space-x-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={syncTaxonomyLevels}
            disabled={syncTaxonomyLevelsMutation.isPending}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${syncTaxonomyLevelsMutation.isPending ? 'animate-spin' : ''}`} />
            Sync Taxonomy Levels
          </Button>
          {!isAddingNew && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setIsAddingNew(true)}
            >
              <PlusCircle className="h-4 w-4 mr-2" />
              Add Objective
            </Button>
          )}
        </div>
      </div>

      {isAddingNew && (
        <Card>
          <CardContent className="pt-6">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Objective Description</FormLabel>
                      <FormControl>
                        <Input placeholder="Students will be able to..." {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="taxonomy_level"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Taxonomy Level</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select taxonomy level" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="remember">Remember</SelectItem>
                          <SelectItem value="understand">Understand</SelectItem>
                          <SelectItem value="apply">Apply</SelectItem>
                          <SelectItem value="analyze">Analyze</SelectItem>
                          <SelectItem value="evaluate">Evaluate</SelectItem>
                          <SelectItem value="create">Create</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="flex justify-end gap-2">
                  <Button 
                    type="button"
                    variant="outline"
                    onClick={resetForm}
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit"
                    disabled={createObjectiveMutation.isPending || updateObjectiveMutation.isPending}
                  >
                    {(createObjectiveMutation.isPending || updateObjectiveMutation.isPending) ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Save className="h-4 w-4 mr-2" />
                    )}
                    {editingObjective ? 'Update Objective' : 'Add Objective'}
                  </Button>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>
      )}

      {objectives && objectives.length > 0 ? (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Description</TableHead>
              <TableHead className="w-[150px]">Taxonomy Level</TableHead>
              <TableHead className="w-[100px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {objectives.map((objective) => (
              <TableRow key={objective.id}>
                <TableCell>{objective.description}</TableCell>
                <TableCell>
                  <TaxonomyBadge taxonomyLevel={objective.taxonomy_level} />
                </TableCell>
                <TableCell>
                  <div className="flex space-x-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={() => setEditingObjective(objective)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-destructive"
                      onClick={() => handleDelete(objective.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <div className="text-center py-6 border rounded-md bg-muted/20">
          <p className="text-muted-foreground">No learning objectives defined yet</p>
          {!isAddingNew && (
            <Button 
              variant="outline" 
              className="mt-4"
              onClick={() => setIsAddingNew(true)}
            >
              <PlusCircle className="h-4 w-4 mr-2" />
              Add First Objective
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default LearningObjectiveManager;
